/**
 * World Map renderer — region grids + pan/zoom (same transform stack as Campaign).
 */

import type { WorldMapBlockView, WorldMapRegionView } from "./types";

export type WorldMapMountOptions = {
  root: HTMLElement;
  regions: WorldMapRegionView[];
  blocks: WorldMapBlockView[];
  selectedId?: string | null;
  onBlockSelect?: (block: WorldMapBlockView) => void;
};

const BLOCK = 28;
const GAP = 3;
const PAD = 18;
const LABEL_H = 22;
const MIN_SCALE = 0.2;
const MAX_SCALE = 2.8;
const WORLD_PAD = 80;

function clampScale(s: number) {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));
}

function blockWorldPos(region: WorldMapRegionView, block: WorldMapBlockView) {
  return {
    x: region.originX + PAD + block.col * (BLOCK + GAP),
    y: region.originY + LABEL_H + PAD + block.row * (BLOCK + GAP),
  };
}

export type WorldMapTreeApi = {
  setData: (regions: WorldMapRegionView[], blocks: WorldMapBlockView[]) => void;
  setSelectedId: (id: string | null) => void;
  destroy: () => void;
  fitView: () => void;
};

export function mountWorldMapView(opts: WorldMapMountOptions): WorldMapTreeApi {
  const { root } = opts;
  root.replaceChildren();
  root.classList.add("worldMapRoot");

  const viewport = document.createElement("div");
  viewport.className = "worldMapViewport";
  const world = document.createElement("div");
  world.className = "worldMapWorld";
  const regionsLayer = document.createElement("div");
  regionsLayer.className = "worldMapRegions";
  const labelsLayer = document.createElement("div");
  labelsLayer.className = "worldMapOwnerLabels";
  world.append(regionsLayer, labelsLayer);
  viewport.appendChild(world);
  root.appendChild(viewport);

  let regions = opts.regions.slice();
  let blocks = opts.blocks.slice();
  let selectedId = opts.selectedId ?? null;
  let scale = 1;
  let panX = 0;
  let panY = 0;
  let suppressClick = false;

  const pointers = new Map<number, { x: number; y: number }>();
  let panPointerId: number | null = null;
  let panStartX = 0;
  let panStartY = 0;
  let panOriginX = 0;
  let panOriginY = 0;
  let pinchActive = false;
  let pinchStartDist = 0;
  let pinchStartScale = 1;
  let pinchStartMidX = 0;
  let pinchStartMidY = 0;
  let pinchStartPanX = 0;
  let pinchStartPanY = 0;

  function applyTransform() {
    world.style.transform = `translate(${panX}px, ${panY}px) scale(${scale})`;
  }

  function worldBounds() {
    if (!regions.length) return { minX: 0, minY: 0, maxX: 800, maxY: 600 };
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const byMode = new Map(regions.map((r) => [r.modeId, r]));
    for (const b of blocks) {
      const region = byMode.get(b.modeId);
      if (!region) continue;
      const p = blockWorldPos(region, b);
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, region.originY);
      maxX = Math.max(maxX, p.x + BLOCK);
      maxY = Math.max(maxY, p.y + BLOCK);
    }
    for (const r of regions) {
      minX = Math.min(minX, r.originX);
      minY = Math.min(minY, r.originY);
      maxX = Math.max(maxX, r.originX + PAD * 2 + 4 * (BLOCK + GAP));
      maxY = Math.max(maxY, r.originY + LABEL_H + PAD * 2 + 4 * (BLOCK + GAP));
    }
    if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 800, maxY: 600 };
    return {
      minX: minX - WORLD_PAD,
      minY: minY - WORLD_PAD,
      maxX: maxX + WORLD_PAD,
      maxY: maxY + WORLD_PAD,
    };
  }

  function fitView() {
    const b = worldBounds();
    const w = Math.max(1, b.maxX - b.minX);
    const h = Math.max(1, b.maxY - b.minY);
    const vw = Math.max(1, viewport.clientWidth);
    const vh = Math.max(1, viewport.clientHeight);
    scale = Math.min(1.2, Math.max(0.28, Math.min(vw / w, vh / h) * 0.9));
    panX = (vw - w * scale) / 2 - b.minX * scale;
    panY = (vh - h * scale) / 2 - b.minY * scale;
    applyTransform();
  }

  function viewportPoint(clientX: number, clientY: number) {
    const rect = viewport.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  function pointerList() {
    return Array.from(pointers.values());
  }

  function twoFingerMetrics() {
    const pts = pointerList();
    if (pts.length < 2) return null;
    const a = pts[0];
    const b = pts[1];
    return {
      dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
      midX: (a.x + b.x) / 2,
      midY: (a.y + b.y) / 2,
    };
  }

  function beginPinch() {
    const m = twoFingerMetrics();
    if (!m) return;
    pinchActive = true;
    panPointerId = null;
    pinchStartDist = m.dist;
    pinchStartScale = scale;
    const mid = viewportPoint(m.midX, m.midY);
    pinchStartMidX = mid.x;
    pinchStartMidY = mid.y;
    pinchStartPanX = panX;
    pinchStartPanY = panY;
    suppressClick = true;
  }

  function updatePinch() {
    const m = twoFingerMetrics();
    if (!m || !pinchActive) return;
    const mid = viewportPoint(m.midX, m.midY);
    const newScale = clampScale(pinchStartScale * (m.dist / pinchStartDist));
    const worldX = (pinchStartMidX - pinchStartPanX) / pinchStartScale;
    const worldY = (pinchStartMidY - pinchStartPanY) / pinchStartScale;
    scale = newScale;
    panX = mid.x - worldX * scale;
    panY = mid.y - worldY * scale;
    applyTransform();
  }

  function renderOwnerLabels() {
    labelsLayer.replaceChildren();
    const byMode = new Map(regions.map((r) => [r.modeId, r]));
    type Acc = { sumX: number; sumY: number; n: number; name: string; color: string };
    const groups = new Map<string, Acc>();

    for (const b of blocks) {
      if (!b.ownerUserId) continue;
      const region = byMode.get(b.modeId);
      if (!region) continue;
      const key = `${b.modeId}::${b.ownerUserId}`;
      const p = blockWorldPos(region, b);
      let g = groups.get(key);
      if (!g) {
        g = {
          sumX: 0,
          sumY: 0,
          n: 0,
          name: b.ownerDisplayName || "Player",
          color: b.ownerColor || "#ffffff",
        };
        groups.set(key, g);
      }
      g.sumX += p.x + BLOCK / 2;
      g.sumY += p.y + BLOCK / 2;
      g.n += 1;
    }

    for (const g of groups.values()) {
      const el = document.createElement("div");
      el.className = "worldMapOwnerLabel";
      el.textContent = g.name;
      el.style.left = `${g.sumX / g.n}px`;
      el.style.top = `${g.sumY / g.n}px`;
      el.style.setProperty("--owner-color", g.color);
      labelsLayer.appendChild(el);
    }
  }

  function render() {
    regionsLayer.replaceChildren();
    const blocksByMode = new Map<string, WorldMapBlockView[]>();
    for (const b of blocks) {
      let list = blocksByMode.get(b.modeId);
      if (!list) {
        list = [];
        blocksByMode.set(b.modeId, list);
      }
      list.push(b);
    }

    for (const region of regions) {
      const wrap = document.createElement("div");
      wrap.className = "worldMapRegion";
      wrap.style.left = `${region.originX}px`;
      wrap.style.top = `${region.originY}px`;
      wrap.style.setProperty("--region-color", region.color);

      const title = document.createElement("div");
      title.className = "worldMapRegionTitle";
      title.textContent = region.label.toUpperCase();
      wrap.appendChild(title);

      const grid = document.createElement("div");
      grid.className = "worldMapRegionGrid";
      const list = blocksByMode.get(region.modeId) || [];
      let maxCol = 0;
      let maxRow = 0;
      for (const b of list) {
        maxCol = Math.max(maxCol, b.col);
        maxRow = Math.max(maxRow, b.row);
      }
      const gridW = PAD * 2 + (maxCol + 1) * (BLOCK + GAP) - GAP;
      const gridH = PAD * 2 + (maxRow + 1) * (BLOCK + GAP) - GAP;
      grid.style.width = `${Math.max(gridW, PAD * 2 + 2 * (BLOCK + GAP))}px`;
      grid.style.height = `${Math.max(gridH, PAD * 2 + 2 * (BLOCK + GAP))}px`;

      for (const b of list) {
        const cell = document.createElement("button");
        cell.type = "button";
        cell.className = "worldMapBlock";
        if (!b.ownerUserId) cell.classList.add("worldMapBlock--neutral");
        else cell.classList.add("worldMapBlock--owned");
        if (b.id === selectedId) cell.classList.add("worldMapBlock--selected");
        cell.style.left = `${PAD + b.col * (BLOCK + GAP)}px`;
        cell.style.top = `${PAD + b.row * (BLOCK + GAP)}px`;
        if (b.ownerColor) cell.style.setProperty("--block-color", b.ownerColor);
        cell.dataset.blockId = b.id;
        cell.title = b.ownerDisplayName
          ? `${b.ownerDisplayName} · ${region.label}`
          : `Neutral · ${region.label}`;
        cell.addEventListener("click", (e) => {
          e.stopPropagation();
          if (suppressClick) return;
          opts.onBlockSelect?.(b);
        });
        grid.appendChild(cell);
      }
      wrap.appendChild(grid);
      regionsLayer.appendChild(wrap);
    }
    renderOwnerLabels();
  }

  function onWheel(e: WheelEvent) {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    const pt = viewportPoint(e.clientX, e.clientY);
    const worldX = (pt.x - panX) / scale;
    const worldY = (pt.y - panY) / scale;
    scale = clampScale(scale * factor);
    panX = pt.x - worldX * scale;
    panY = pt.y - worldY * scale;
    applyTransform();
  }

  function onPointerDown(e: PointerEvent) {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) {
      beginPinch();
      return;
    }
    if (pointers.size === 1 && !pinchActive) {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.(".worldMapBlock")) return;
      panPointerId = e.pointerId;
      panStartX = e.clientX;
      panStartY = e.clientY;
      panOriginX = panX;
      panOriginY = panY;
      suppressClick = false;
    }
  }

  function onPointerMove(e: PointerEvent) {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinchActive && pointers.size >= 2) {
      updatePinch();
      return;
    }
    if (panPointerId === e.pointerId) {
      const dx = e.clientX - panStartX;
      const dy = e.clientY - panStartY;
      if (Math.hypot(dx, dy) > 4) suppressClick = true;
      panX = panOriginX + dx;
      panY = panOriginY + dy;
      applyTransform();
    }
  }

  function onPointerUp(e: PointerEvent) {
    pointers.delete(e.pointerId);
    if (panPointerId === e.pointerId) panPointerId = null;
    if (pointers.size < 2) pinchActive = false;
    if (pointers.size === 1 && !pinchActive) {
      const [id, pt] = [...pointers.entries()][0];
      panPointerId = id;
      panStartX = pt.x;
      panStartY = pt.y;
      panOriginX = panX;
      panOriginY = panY;
    }
    window.setTimeout(() => {
      suppressClick = false;
    }, 80);
  }

  viewport.addEventListener("wheel", onWheel, { passive: false });
  viewport.addEventListener("pointerdown", onPointerDown);
  window.addEventListener("pointermove", onPointerMove);
  window.addEventListener("pointerup", onPointerUp);
  window.addEventListener("pointercancel", onPointerUp);

  render();
  requestAnimationFrame(() => fitView());

  return {
    setData(nextRegions, nextBlocks) {
      regions = nextRegions.slice();
      blocks = nextBlocks.slice();
      render();
    },
    setSelectedId(id) {
      selectedId = id;
      render();
    },
    destroy() {
      viewport.removeEventListener("wheel", onWheel);
      viewport.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      root.replaceChildren();
      root.classList.remove("worldMapRoot");
    },
    fitView,
  };
}
